import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useOperator } from '@/lib/operator-context';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Globe, BarChart3 } from 'lucide-react';

import {
  FlowCanvas,
  FlowLegend,
  PhoneMockup,
  FlowStagePanel,
  FlowFunnelChart,
} from '@/components/flow-builder';
import { NativeFlowPreview } from '@/components/flow-builder/NativeFlowPreview';
import { FunnelChartSkeleton } from '@/components/flow-builder/FlowSkeletons';
import { useFlowData } from '@/lib/flow-builder/useFlowData';
import { useFlowCounts, useFunnelData } from '@/hooks/useFlowData';
import type { FlowState, FlowStateType } from '@/lib/flow-builder/types';
import { STATE_TYPE_MAP } from '@/lib/flow-builder/types';

import type { FlowTabProps } from './types';

// ============================================================================
// FlowVisualTab -- Visual canvas with phone mockup and funnel analytics
// ============================================================================

export function FlowVisualTab({ flow, flowSlug, businessId }: FlowTabProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedBusiness } = useOperator();

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------
  const [locale, setLocale] = useState<'en' | 'pt-BR'>('en');
  const [selectedNodeId, setSelectedNodeId] = useState<FlowState | null>(null);
  const [stagePanelOpen, setStagePanelOpen] = useState(false);
  const [stagePanelNodeId, setStagePanelNodeId] = useState<FlowState | null>(null);

  // ---------------------------------------------------------------------------
  // Data hooks
  // ---------------------------------------------------------------------------
  const { counts: conversationCounts } = useFlowCounts({
    flowId: flowSlug,
    enabled: true,
    refetchInterval: 30000,
  });

  const { stages: funnelData, isLoading: funnelLoading } = useFunnelData({
    flowId: flowSlug,
    enabled: true,
    refetchInterval: 60000,
  });

  const { getNodeById, nodes: flowNodes } = useFlowData({
    businessType: 'barbershop',
    locale,
    flowSlug,
  });

  const selectedNodeData = selectedNodeId
    ? getNodeById(selectedNodeId)?.data ?? null
    : null;

  const businessName =
    selectedBusiness?.name || t('flowBuilder.businessNames.barbershop');

  const flowDisplayName =
    locale === 'pt-BR' && flow.namePtBr ? flow.namePtBr : flow.name;

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const toggleLocale = useCallback(() => {
    setLocale((prev) => (prev === 'en' ? 'pt-BR' : 'en'));
  }, []);

  const handleNodeClick = useCallback((nodeId: string) => {
    setSelectedNodeId(nodeId as FlowState);
  }, []);

  const handleBadgeClick = useCallback((nodeId: string) => {
    setStagePanelNodeId(nodeId as FlowState);
    setStagePanelOpen(true);
  }, []);

  const handleStageClick = useCallback((stage: FlowState) => {
    setStagePanelNodeId(stage);
    setStagePanelOpen(true);
  }, []);

  const handleStagePanelClose = useCallback(() => {
    setStagePanelOpen(false);
  }, []);

  const handleConversationSelect = useCallback(
    (conversationId: string) => {
      setStagePanelOpen(false);
      navigate({ to: `/operator/conversations/${conversationId}` });
    },
    [navigate],
  );

  // ---------------------------------------------------------------------------
  // Stage panel data
  // ---------------------------------------------------------------------------
  const stagePanelData = useMemo(() => {
    if (!stagePanelNodeId) return null;
    const node = getNodeById(stagePanelNodeId);
    return {
      stageName: node?.data.title || stagePanelNodeId,
      stateType:
        (STATE_TYPE_MAP[stagePanelNodeId] as FlowStateType) ||
        ('interaction' as FlowStateType),
    };
  }, [stagePanelNodeId, getNodeById]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="space-y-6" data-testid="flow-visual-tab">
      {/* Controls bar */}
      <div className="flex items-center justify-end gap-3">
        <Button
          variant="outline"
          size="sm"
          onClick={toggleLocale}
          className="flex items-center gap-2"
          data-testid="locale-toggle"
        >
          <Globe className="h-4 w-4" />
          {locale === 'en' ? 'EN' : 'PT'}
        </Button>
      </div>

      {/* Canvas + Phone Mockup grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Flow Canvas */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">
              {t('flowBuilder.canvasTitle')}
            </CardTitle>
            <CardDescription>
              {t('flowBuilder.canvasDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="h-[500px] border-t">
              <FlowCanvas
                locale={locale}
                readOnly={true}
                showControls={true}
                showMiniMap={false}
                onNodeClick={handleNodeClick}
                onBadgeClick={handleBadgeClick}
                conversationCounts={conversationCounts}
                flowNodes={flowNodes}
              />
            </div>
          </CardContent>
        </Card>

        {/* Right Sidebar -- Phone Mockup */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">
                {t('flowBuilder.previewTitle')}
              </CardTitle>
              <CardDescription>
                {selectedNodeId
                  ? t('flowBuilder.previewSelectedNode', {
                      node: selectedNodeData?.title || selectedNodeId,
                    })
                  : t('flowBuilder.previewSelectNode')}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex justify-center">
              <PhoneMockup
                selectedNodeId={selectedNodeId}
                nodeData={selectedNodeData}
                businessName={businessName}
                locale={locale}
              />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* WhatsApp Native Flow Preview (actual screen mockups) */}
      {flow.flowJson ? (
        <NativeFlowPreview flowJson={flow.flowJson as any} />
      ) : null}

      {/* Funnel Analytics */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-lg">
              {t('flowBuilder.funnel.title')}
            </CardTitle>
          </div>
          <CardDescription>
            {t('flowBuilder.funnel.description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {funnelLoading && <FunnelChartSkeleton stageCount={6} />}

          {!funnelLoading && funnelData && funnelData.length > 0 && (
            <FlowFunnelChart
              stages={funnelData}
              flowName={flowDisplayName}
              showDropOff={true}
              onStageClick={handleStageClick}
            />
          )}

          {!funnelLoading && (!funnelData || funnelData.length === 0) && (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <BarChart3 className="h-8 w-8 mb-2 opacity-50" />
              <p className="text-sm">{t('flowBuilder.funnel.noData')}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <FlowLegend defaultExpanded={true} className="max-w-2xl" />

      {/* Stage Panel overlay */}
      {stagePanelNodeId && stagePanelData && (
        <FlowStagePanel
          flowId={flowSlug}
          stageId={stagePanelNodeId}
          stageName={stagePanelData.stageName}
          stateType={stagePanelData.stateType}
          open={stagePanelOpen}
          onConversationSelect={handleConversationSelect}
          onClose={handleStagePanelClose}
          locale={locale}
          useDataHook={true}
        />
      )}
    </div>
  );
}

export default FlowVisualTab;
